'use client';

import { FC, useState } from 'react';
import { FeedPostContent, FeedEntry } from '@/types/feed';
import {
  BaseFeedItem,
  TitleSection,
  ImageSection,
  MetadataSection,
  PrimaryActionSection,
} from '@/components/Feed/BaseFeedItem';
import { Avatar } from '@/components/ui/Avatar';
import { AvatarStack } from '@/components/ui/AvatarStack';
import { Button } from '@/components/ui/Button';
import { ContributeToFundraiseModal } from '@/components/modals/ContributeToFundraiseModal';
import { AuthorTooltip } from '@/components/ui/AuthorTooltip';
import { FeedItemFundingBadges } from '@/components/Feed/FeedItemFundingBadges';
import { PeerReviewTooltip } from '@/components/tooltips/PeerReviewTooltip';
import { Tooltip } from '@/components/ui/Tooltip';
import { Pin, ArrowRight, Star } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { buildWorkUrl } from '@/utils/url';
import { useCurrencyPreference } from '@/contexts/CurrencyPreferenceContext';
import { useExchangeRate } from '@/contexts/ExchangeRateContext';
import { useShareModalContext } from '@/contexts/ShareContext';
import { useUser } from '@/contexts/UserContext';
import { AiVerdictBadge } from '@/components/Feed/AiVerdictBadge';
import { formatCurrency } from '@/utils/currency';
import { isDeadlineInFuture } from '@/utils/date';
import Link from 'next/link';

interface FeedItemFundraiseProps {
  entry: FeedEntry;
  href?: string;
  showTooltips?: boolean;
  showActions?: boolean;
  showHeader?: boolean;
  maxLength?: number;
  customActionText?: string;
  isPinnedFundraise?: boolean;
  onFeedItemClick?: () => void;
  showBountyInfo?: boolean;
}

export const FeedItemFundraise: FC<FeedItemFundraiseProps> = ({
  entry,
  href,
  showTooltips = true,
  showActions = true,
  showHeader = true,
  maxLength,
  customActionText,
  isPinnedFundraise = false,
  onFeedItemClick,
  showBountyInfo,
}) => {
  const router = useRouter();
  const { showUSD } = useCurrencyPreference();
  const { exchangeRate } = useExchangeRate();
  const { showShareModal } = useShareModalContext();
  const { user } = useUser();
  const [isContributeModalOpen, setIsContributeModalOpen] = useState(false);
  const showAiVerdict = !!user?.isModerator && !!entry.aiPeerReview?.overallRating;

  const post = entry.content as FeedPostContent;
  const hasFundraise = post.contentType === 'PREREGISTRATION' && post.fundraise;
  const isNonprofit =
    entry.raw?.is_nonprofit === true && post.contentType === 'PREREGISTRATION' && post.fundraise;

  const primaryAuthor = post.authors?.[0];
  const reviewScore = entry.metrics?.reviewScore;
  const hasReviewScore = reviewScore !== undefined && reviewScore > 0;

  const fundingPageUrl =
    href ||
    buildWorkUrl({
      id: post.id,
      slug: post.slug,
      contentType: 'preregistration',
    });

  const imageUrl = post.previewImage ?? undefined;

  const fundraise = post.fundraise;
  const isActive =
    fundraise &&
    fundraise.status === 'OPEN' &&
    (fundraise.endDate ? isDeadlineInFuture(fundraise.endDate) : true);

  const contributors =
    fundraise?.contributors?.topContributors?.map((c) => ({
      src: c.authorProfile.profileImage || '',
      alt: c.authorProfile.fullName,
      tooltip: c.authorProfile.fullName,
      authorId: c.authorProfile.id || undefined,
    })) || [];

  const handleContributeSuccess = () => {
    setIsContributeModalOpen(false);
    showShareModal({
      url: window.location.href,
      docTitle: post.title,
      action: 'USER_FUNDED_PROPOSAL',
    });
    router.refresh();
  };

  return (
    <>
      <BaseFeedItem
        entry={entry}
        href={fundingPageUrl}
        showActions={showActions}
        showTooltips={showTooltips}
        customActionText={
          customActionText ?? (hasFundraise ? `is seeking funding` : 'published a post')
        }
        maxLength={maxLength}
        onFeedItemClick={onFeedItemClick}
        showBountyInfo={showBountyInfo}
        showHeader={false}
        hideReportButton={false}
        cardImageLeft={
          imageUrl ? (
            <>
              <ImageSection
                imageUrl={imageUrl}
                fullSizeImageUrl={imageUrl}
                alt={post.title || 'Fundraise image'}
                naturalDimensions
                previewOnClick={false}
              />
              <div className="absolute top-2 left-2 flex items-center gap-1.5">
                {showAiVerdict && (
                  <AiVerdictBadge rating={entry.aiPeerReview?.overallRating ?? null} />
                )}
                <FeedItemFundingBadges
                  reviews={post.reviews}
                  href={fundingPageUrl}
                  isNonprofit={!!isNonprofit}
                  fundraiseStatus={fundraise?.status}
                  variant="overlay"
                />
              </div>
            </>
          ) : undefined
        }
      >
        {isPinnedFundraise && (
          <div className="absolute top-3 right-3 z-10 pointer-events-none">
            <Pin className="w-4 h-4 text-blue-600" />
          </div>
        )}

        {/* Mobile image */}
        {imageUrl && (
          <div className="md:!hidden w-[calc(100%+2rem)] mb-5 -mx-4 -mt-4 overflow-hidden">
            <ImageSection
              imageUrl={imageUrl}
              fullSizeImageUrl={imageUrl}
              alt={post.title || 'Fundraise image'}
              aspectRatio="16/9"
              previewOnClick={true}
            />
          </div>
        )}

        <TitleSection
          title={post.title}
          href={fundingPageUrl}
          onClick={onFeedItemClick}
          className="text-md md:!text-md"
        />

        <MetadataSection className="mb-0 py-2">
          {primaryAuthor ? (
            <div className="flex items-center gap-2.5">
              <AuthorTooltip authorId={primaryAuthor.id !== 0 ? primaryAuthor.id : undefined}>
                <Avatar
                  src={primaryAuthor.profileImage || undefined}
                  alt={primaryAuthor.fullName}
                  size="sm"
                  disableTooltip
                />
              </AuthorTooltip>
              <div className="flex flex-col min-w-0">
                <Link
                  href={primaryAuthor.profileUrl || '#'}
                  className="text-sm font-medium text-gray-900 hover:underline truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  {primaryAuthor.fullName}
                </Link>
                <span className="text-xs text-gray-500 truncate">
                  {entry.nonprofit?.name || post.institution}
                </span>
              </div>
            </div>
          ) : (
            <span className="text-sm text-gray-500">
              {entry.nonprofit?.name || post.institution}
            </span>
          )}
        </MetadataSection>

        {hasFundraise && fundraise && (
          <PrimaryActionSection>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-6 min-w-0">
                <div className="flex flex-col leading-tight whitespace-nowrap">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">Requested</span>
                  <span className="font-mono font-semibold text-primary-600 text-xl">
                    {formatCurrency({
                      amount:
                        fundraise.status === 'COMPLETED'
                          ? Math.round(
                              showUSD ? fundraise.goalAmount.usd : fundraise.amountRaised.rsc
                            )
                          : Math.round(
                              showUSD ? fundraise.goalAmount.usd : fundraise.goalAmount.rsc
                            ),
                      showUSD,
                      exchangeRate,
                      skipConversion: true,
                      shorten: true,
                    })}
                  </span>
                </div>

                {contributors.length > 0 && (
                  <div className="hidden sm:flex flex-col leading-tight">
                    <span className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                      Backers
                    </span>
                    <AvatarStack
                      items={contributors}
                      size="xs"
                      maxItems={3}
                      spacing={-6}
                      showLabel={false}
                      disableTooltip={false}
                      showExtraCount={true}
                      totalItemsCount={fundraise.contributors.numContributors}
                      extraCountLabel="Backers"
                    />
                  </div>
                )}

                {hasReviewScore && (
                  <div className="hidden sm:flex flex-col leading-tight whitespace-nowrap">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                      Peer Review
                    </span>
                    <Tooltip
                      content={
                        <PeerReviewTooltip
                          reviews={post.reviews ?? []}
                          averageScore={reviewScore!}
                          href={fundingPageUrl}
                        />
                      }
                      position="top"
                      width="w-[320px]"
                    >
                      <span className="inline-flex items-center gap-1 text-xl font-semibold text-gray-900 cursor-help">
                        <Star size={18} className="fill-amber-400 text-amber-400" />
                        {reviewScore!.toFixed(1)}
                      </span>
                    </Tooltip>
                  </div>
                )}
              </div>

              {isActive ? (
                <Button
                  variant="dark"
                  size="sm"
                  className="flex-shrink-0 gap-1"
                  onClick={() => setIsContributeModalOpen(true)}
                >
                  Fund
                  <ArrowRight size={14} />
                </Button>
              ) : (
                <span className="flex-shrink-0 text-sm text-gray-400">Ended</span>
              )}
            </div>
          </PrimaryActionSection>
        )}
      </BaseFeedItem>

      {hasFundraise && fundraise && (
        <ContributeToFundraiseModal
          isOpen={isContributeModalOpen}
          onClose={() => setIsContributeModalOpen(false)}
          onContributeSuccess={handleContributeSuccess}
          fundraise={fundraise}
          proposalTitle={post.title}
        />
      )}
    </>
  );
};
